import { useState, useRef, useEffect } from 'react';
import Canvas from './components/Canvas';
import ContextMenu from './components/ContextMenu';
import EditorDialog from './components/EditorDialog';
import EditorSidebar from './components/EditorSidebar';
import RenameSeatsModal from './components/RenameSeatsModal';
import { useHistory } from './hooks/useHistory';
import { useSelection } from './hooks/useSelection';
import { useSeatManagement } from './hooks/useSeatManagement';
import { useZoom } from './hooks/useZoom';
import {
  getGridPosition,
  getSeatAtPosition,
  getSeatAtPositionWithDetached,
  isPositionOccupied,
  addTheaterLayout,
  addRowFromPosition,
  translatePolygonPoints,
  clientToCanvasCoords,
  formatSeatLabel,
  getSeatNumberForCol,
  DEFAULT_NAMING,
  relabelSeats,
  toggleInList,
  isSkippedCol,
  isSkippedRow,
  GRID_SIZE,
  DEFAULT_GRID_COLS,
  GRID_ROWS as DEFAULT_GRID_ROWS,
  countZoneSeatSlots,
  distributeTargetCapacity,
  sumZoneCapacities,
  sumZoneMaxSlots,
  hydrateSeatsFromApi,
} from './utils/seatUtils';
import { LAYOUT_TEMPLATES, buildTemplateZones } from './utils/layoutTemplates';

export default function AdvancedSeatEditor({
  onSeatsCreate,
  existingSeats = [],
  zones = [],
  useZones = false,
  layoutType = 'SEAT_PLAN',
  onZonesUpdate,
  onNext,
  onPrevious,
  initialBackgroundImage = null,
  onBackgroundChange,
  initialNaming = null,
  onNamingChange,
  initialTemplateId = 'blank',
}) {
  const [seats, setSeats] = useState(() => hydrateSeatsFromApi(existingSeats));
  const [currentZones, setCurrentZones] = useState(zones || []);
  // If zones exist, default to first zone for seat creation
  const [selectedZone, setSelectedZone] = useState(() => {
    if (useZones && zones && zones.length > 0) {
      return zones[0];
    }
    return null;
  }); // Currently editing zone
  const [highlightedZone, setHighlightedZone] = useState(null); // Currently selected/highlighted zone
  const [zoneMode, setZoneMode] = useState(useZones ? 'zone-overview' : 'seat-design'); // 'zone-overview' | 'zone-edit' | 'seat-design'
  const [isCreatingZone, setIsCreatingZone] = useState(false);
  const [isDrawingZone, setIsDrawingZone] = useState(false);
  const [zoneDragStart, setZoneDragStart] = useState(null);
  const [previewZone, setPreviewZone] = useState(null);
  const [zoneDrawingMode, setZoneDrawingMode] = useState('polygon'); // default freeform for stadiums
  const [polygonPoints, setPolygonPoints] = useState([]); // For polygon drawing
  const [isPolygonMode, setIsPolygonMode] = useState(false);
  const [isDraggingZone, setIsDraggingZone] = useState(false);
  const [draggedZone, setDraggedZone] = useState(null);
  const [zoneDragOffset, setZoneDragOffset] = useState({ x: 0, y: 0 });
  const [newZone, setNewZone] = useState({
    name: '',
    description: '',
    color: '#3B82F6',
    capacity: '',
    sortOrder: 0,
  });

  const [addSeatMode, setAddSeatMode] = useState(true);
  const [selectedSeatType, setSelectedSeatType] = useState('REGULAR');
  const [isDrawing, setIsDrawing] = useState(false);
  const [showBulkTools, setShowBulkTools] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartPos, setDragStartPos] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [positioningMode, setPositioningMode] = useState(false);
  const [positioningSeats, setPositioningSeats] = useState([]);
  const [isFullscreen, setIsFullscreen] = useState(true);
  const [gridCols, setGridCols] = useState(DEFAULT_GRID_COLS);
  const [gridRows, setGridRows] = useState(DEFAULT_GRID_ROWS);
  const [ghostPreviewSeats, setGhostPreviewSeats] = useState([]);
  const [activeTool, setActiveTool] = useState('add');
  const [seatTool, setSeatTool] = useState('paint');
  const [hoverCell, setHoverCell] = useState(null);
  const [boxStart, setBoxStart] = useState(null);
  const [boxEnd, setBoxEnd] = useState(null);
  const paintBufferRef = useRef([]);
  const seatsRef = useRef(seats);
  seatsRef.current = seats;
  const currentZonesRef = useRef(currentZones);
  currentZonesRef.current = currentZones;
  const namingRef = useRef(naming);
  namingRef.current = naming;
  const saveToHistoryRef = useRef(null);
  const [positioningError, setPositioningError] = useState(null);
  const [backgroundImage, setBackgroundImage] = useState(initialBackgroundImage);
  const [backgroundOpacity, setBackgroundOpacity] = useState(0.85);
  const [naming, setNaming] = useState(() => ({
    ...DEFAULT_NAMING,
    ...(initialNaming && typeof initialNaming === 'object' ? initialNaming : {}),
    aisleCols: [...(initialNaming?.aisleCols || [])],
    aisleCells: [...(initialNaming?.aisleCells || [])],
    voidCols: [...(initialNaming?.voidCols || [])],
    voidRows: [...(initialNaming?.voidRows || [])],
  }));
  const [aisleColInput, setAisleColInput] = useState('');
  const [targetCapacity, setTargetCapacity] = useState('');
  const [showAllZoneSeats, setShowAllZoneSeats] = useState(false);
  const isAreaLayout = layoutType === 'AREA_LAYOUT';
  const [activeTemplateId, setActiveTemplateId] = useState(initialTemplateId || 'blank');
  const [editorDialog, setEditorDialog] = useState(null);
  const [renameModal, setRenameModal] = useState(null);

  const showAlert = (message, title = 'Bilgi') =>
    new Promise((resolve) => {
      setEditorDialog({
        type: 'alert',
        title,
        message,
        resolve,
      });
    });

  const showConfirm = (message, title = 'Onay', { danger = false, confirmLabel = 'Evet' } = {}) =>
    new Promise((resolve) => {
      setEditorDialog({
        type: 'confirm',
        title,
        message,
        danger,
        confirmLabel,
        resolve,
      });
    });

  const seatKeyOf = (seat) =>
    seat.isDetached ? `${seat.row}-${seat.col}` : `${Math.round(seat.row)}-${Math.round(seat.col)}`;

  const gridRef = useRef(null);
  const containerRef = useRef(null);
  const zoneDragMovedRef = useRef(false);
  const zoneDragOriginRef = useRef(null);

  // Zone navigation functions
  const enterZone = (zone) => {
    if (!zone || zone.isStage) return;
    // Always enter edit so seats can be painted inside the block
    const fresh = currentZones.find((z) => z.id === zone.id) || zone;
    setSelectedZone(fresh);
    setHighlightedZone(fresh);
    setZoneMode('zone-edit');
    setShowAllZoneSeats(false);
    setActiveTool('add');
    setAddSeatMode(true);
    setSeatTool('paint');
  };

  const backToZoneOverview = () => {
    setZoneMode('zone-overview');
    setSelectedZone(null);
    setHighlightedZone(null);
    setAddSeatMode(false);
    setShowAllZoneSeats(false);
  };

  // Zone management functions
  const handleZoneCanvasMouseDown = (e) => {
    if (!isCreatingZone || zoneMode !== 'zone-overview') return;
    if (!gridRef.current) return;

    e.preventDefault();
    e.stopPropagation();

    const canvas = clientToCanvasCoords(e.clientX, e.clientY, gridRef, zoom);
    if (!canvas) return;
    const x = canvas.x;
    const y = canvas.y;

    if (zoneDrawingMode === 'polygon') {
      // Polygon mode - add point on each click
      if (polygonPoints.length === 0) {
        // First point - start polygon
        setPolygonPoints([{ x, y }]);
        setIsPolygonMode(true);
      } else {
        // Check if clicking near first point to close polygon
        const firstPoint = polygonPoints[0];
        const distance = Math.sqrt(Math.pow(x - firstPoint.x, 2) + Math.pow(y - firstPoint.y, 2));

        if (distance < 30 && polygonPoints.length >= 3) {
          // Close polygon and create zone
          finishPolygonZone();
        } else {
          // Add new point
          setPolygonPoints((prev) => [...prev, { x, y }]);
        }
      }
    } else {
      // Rectangle/Circle mode
      setZoneDragStart({ x, y });
      setIsDrawingZone(true);
      setPreviewZone({
        x,
        y,
        width: 0,
        height: 0,
        ...newZone,
      });
    }
  };

  const finishPolygonZone = () => {
    if (polygonPoints.length < 3) return;

    // Calculate bounding box for polygon
    const xs = polygonPoints.map((p) => p.x);
    const ys = polygonPoints.map((p) => p.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);

    const finalZone = {
      id: `zone_${Date.now()}`,
      name: newZone.name || `Zone ${currentZones.length + 1}`,
      description: newZone.description,
      color: newZone.color,
      type: 'POLYGON',
      capacity: newZone.capacity ? parseInt(newZone.capacity) : null,
      sortOrder: currentZones.length,
      isActive: true,
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY,
      polygonPoints: polygonPoints, // Store polygon points
      seats: [],
    };

    const updatedZones = [...currentZones, finalZone];
    setCurrentZones(updatedZones);
    onZonesUpdate && onZonesUpdate(updatedZones);
    saveToHistory(seats, `Zone oluÅŸturuldu: ${finalZone.name}`, updatedZones);

    // If this is the first zone and no zone is selected, select it for seat creation
    if (currentZones.length === 0 && !selectedZone) {
      setSelectedZone(finalZone);
    }

    // Reset states
    setPolygonPoints([]);
    setIsPolygonMode(false);
    setIsCreatingZone(false);
    setNewZone({
      name: '',
      description: '',
      color: '#3B82F6',
      capacity: '',
      sortOrder: currentZones.length + 1,
    });
  };

  const handleZoneCanvasMouseMove = (e) => {
    if (!isDrawingZone || !zoneDragStart || zoneMode !== 'zone-overview') return;

    const canvas = clientToCanvasCoords(e.clientX, e.clientY, gridRef, zoom);
    if (!canvas) return;
    const currentX = canvas.x;
    const currentY = canvas.y;

    if (zoneDrawingMode === 'rectangle') {
      const width = Math.abs(currentX - zoneDragStart.x);
      const height = Math.abs(currentY - zoneDragStart.y);
      const x = Math.min(zoneDragStart.x, currentX);
      const y = Math.min(zoneDragStart.y, currentY);

      setPreviewZone({
        ...previewZone,
        x,
        y,
        width,
        height,
      });
    } else if (zoneDrawingMode === 'circle') {
      const deltaX = currentX - zoneDragStart.x;
      const deltaY = currentY - zoneDragStart.y;
      const radius = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

      setPreviewZone({
        ...previewZone,
        x: zoneDragStart.x - radius,
        y: zoneDragStart.y - radius,
        width: radius * 2,
        height: radius * 2,
        isCircle: true,
      });
    }
  };

  const handleZoneCanvasMouseUp = (e) => {
    if (!isDrawingZone || !previewZone || zoneMode !== 'zone-overview') return;

    // Minimum size check
    if (previewZone.width < 50 || previewZone.height < 50) {
      setIsDrawingZone(false);
      setPreviewZone(null);
      setZoneDragStart(null);
      return;
    }

    // Create the zone
    const finalZone = {
      id: `zone_${Date.now()}`,
      name: newZone.name || `Zone ${currentZones.length + 1}`,
      description: newZone.description,
      color: newZone.color,
      type: zoneDrawingMode.toUpperCase(),
      capacity: newZone.capacity ? parseInt(newZone.capacity) : null,
      sortOrder: currentZones.length,
      isActive: true,
      x: previewZone.x,
      y: previewZone.y,
      width: previewZone.width,
      height: previewZone.height,
      isCircle: zoneDrawingMode === 'circle', // Circle flag
      seats: [],
    };

    const updatedZones = [...currentZones, finalZone];
    setCurrentZones(updatedZones);
    onZonesUpdate && onZonesUpdate(updatedZones);
    saveToHistory(seats, `Zone oluÅŸturuldu: ${finalZone.name}`, updatedZones);

    // If this is the first zone and no zone is selected, select it for seat creation
    if (currentZones.length === 0 && !selectedZone) {
      setSelectedZone(finalZone);
    }

    // Reset states
    setIsDrawingZone(false);
    setPreviewZone(null);
    setZoneDragStart(null);
    setIsCreatingZone(false);
    setNewZone({
      name: '',
      description: '',
      color: '#3B82F6',
      capacity: '',
      sortOrder: currentZones.length + 1,
    });
  };

  const handleZoneClick = (zone, clientX, clientY) => {
    if (zone?.isStage) {
      setHighlightedZone(zone);
      return;
    }
    // After a drag, ignore the synthetic click so we don't enter edit mid-move
    if (zoneDragMovedRef.current) {
      zoneDragMovedRef.current = false;
      setHighlightedZone(zone);
      return;
    }
    if (zoneMode === 'zone-overview') {
      enterZone(zone);
      return;
    }
    if (zoneMode === 'zone-edit' && selectedZone?.id === zone.id) {
      return;
    }
  };

  const handleZoneDoubleClick = (zone) => {
    if (zone?.isStage) return;
    enterZone(zone);
  };

  const handleZoneMouseDown = (zone, e) => {
    const canDrag =
      zoneMode === 'zone-overview' && !zone.isStage && !isCreatingZone && activeTool !== 'hand';
    if (!canDrag) return;

    zoneDragMovedRef.current = false;
    const snapZone = {
      ...zone,
      polygonPoints: Array.isArray(zone.polygonPoints)
        ? zone.polygonPoints.map((p) => ({ ...p }))
        : zone.polygonPoints,
      points: Array.isArray(zone.points) ? zone.points.map((p) => ({ ...p })) : zone.points,
    };
    zoneDragOriginRef.current = {
      zone: snapZone,
      seats: seatsRef.current.filter((s) => s.zoneId === zone.id).map((s) => ({ ...s })),
    };
    setHighlightedZone(zone);
    setIsDraggingZone(true);
    setDraggedZone(zone);
    const canvas = clientToCanvasCoords(e.clientX, e.clientY, gridRef, zoom);
    if (canvas) {
      setZoneDragOffset({
        x: canvas.x - (zone.x || 0),
        y: canvas.y - (zone.y || 0),
      });
    }
    e.preventDefault();
    e.stopPropagation();
  };

  const handleZoneMouseMove = (e) => {
    if (!isDraggingZone || !draggedZone || !zoneDragOriginRef.current) return;

    const canvas = clientToCanvasCoords(e.clientX, e.clientY, gridRef, zoom);
    if (!canvas) return;

    const origin = zoneDragOriginRef.current;
    const newX = Math.max(0, canvas.x - zoneDragOffset.x);
    const newY = Math.max(0, canvas.y - zoneDragOffset.y);
    const totalDx = newX - (origin.zone.x || 0);
    const totalDy = newY - (origin.zone.y || 0);

    if (Math.abs(totalDx) > 4 || Math.abs(totalDy) > 4) {
      zoneDragMovedRef.current = true;
    }

    const updatedZones = currentZones.map((zone) => {
      if (zone.id !== draggedZone.id) return zone;
      const next = { ...zone, x: newX, y: newY };
      if (Array.isArray(origin.zone.polygonPoints) && origin.zone.polygonPoints.length) {
        next.polygonPoints = translatePolygonPoints(origin.zone.polygonPoints, totalDx, totalDy);
      }
      if (Array.isArray(origin.zone.points) && origin.zone.points.length) {
        next.points = translatePolygonPoints(origin.zone.points, totalDx, totalDy);
      }
      return next;
    });

    const updatedSeats = seatsRef.current.map((seat) => {
      if (seat.zoneId !== draggedZone.id) return seat;
      const orig = origin.seats.find((s) => s.id === seat.id);
      if (!orig) return seat;
      const baseX = orig.x ?? orig.col * GRID_SIZE;
      const baseY = orig.y ?? orig.row * GRID_SIZE;
      const px = baseX + totalDx;
      const py = baseY + totalDy;
      const newCol = Math.round(px / GRID_SIZE);
      const newRow = Math.round(py / GRID_SIZE);
      return {
        ...seat,
        x: newCol * GRID_SIZE,
        y: newRow * GRID_SIZE,
        col: newCol,
        row: newRow,
        label: formatSeatLabel(newRow, newCol, naming),
        number: getSeatNumberForCol(newCol, naming, newRow),
      };
    });

    const movedZone = updatedZones.find((z) => z.id === draggedZone.id);
    setDraggedZone(movedZone || { ...draggedZone, x: newX, y: newY });
    setHighlightedZone(movedZone || null);
    setSelectedZone((prev) => (prev && prev.id === draggedZone.id ? movedZone || prev : prev));
    setCurrentZones(updatedZones);
    currentZonesRef.current = updatedZones;
    onZonesUpdate && onZonesUpdate(updatedZones);
    seatsRef.current = updatedSeats;
    setSeats(updatedSeats);
    onSeatsCreate(updatedSeats);
  };

  const handleZoneMouseUp = () => {
    if (!isDraggingZone) return;
    const didMove = zoneDragMovedRef.current;
    setIsDraggingZone(false);
    setDraggedZone(null);
    setZoneDragOffset({ x: 0, y: 0 });
    zoneDragOriginRef.current = null;
    if (didMove && saveToHistoryRef.current) {
      saveToHistoryRef.current(seatsRef.current, 'Zone taşındı', currentZonesRef.current);
    }
  };

  const deleteZone = async (zoneId) => {
    const zoneToDelete = currentZones.find((zone) => zone.id === zoneId);
    const ok = await showConfirm("Bu zone'u silmek istediğinizden emin misiniz?", 'Zone sil', {
      danger: true,
      confirmLabel: 'Sil',
    });
    if (!ok) return;
    const filteredZones = currentZones.filter((zone) => zone.id !== zoneId);
    const cleanedSeats = seats.filter((s) => s.zoneId !== zoneId);
    setCurrentZones(filteredZones);
    setSeats(cleanedSeats);
    onSeatsCreate(cleanedSeats);
    onZonesUpdate && onZonesUpdate(filteredZones);
    saveToHistory(
      cleanedSeats,
      `Zone silindi: ${zoneToDelete?.name || 'Bilinmeyen'}`,
      filteredZones,
    );
    if (selectedZone?.id === zoneId) {
      setSelectedZone(null);
    }
    if (highlightedZone?.id === zoneId) {
      setHighlightedZone(null);
    }
  };

  const zoneClipboardRef = useRef(null);
  const [zoneClipboardReady, setZoneClipboardReady] = useState(false);

  const cloneZoneGeometry = (zone, dx = 48, dy = 48) => {
    const next = {
      ...zone,
      id: `zone-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
      name: `${zone.name || 'Blok'} kopya`,
      x: (Number(zone.x) || 0) + dx,
      y: (Number(zone.y) || 0) + dy,
      sortOrder: (currentZones?.length || 0) + 1,
    };
    if (Array.isArray(zone.polygonPoints) && zone.polygonPoints.length) {
      next.polygonPoints = translatePolygonPoints(zone.polygonPoints, dx, dy);
    }
    if (Array.isArray(zone.points) && zone.points.length) {
      next.points = translatePolygonPoints(zone.points, dx, dy);
    }
    return next;
  };

  const copyZone = (zone) => {
    if (!zone || zone.isStage) return;
    zoneClipboardRef.current = JSON.parse(JSON.stringify(zone));
    setZoneClipboardReady(true);
  };

  const pasteZone = () => {
    const src = zoneClipboardRef.current;
    if (!src) {
      showAlert('Önce bir zone kopyala (Ctrl+C veya Kopyala).');
      return;
    }
    const clone = cloneZoneGeometry(src);
    const next = [...currentZones, clone];
    setCurrentZones(next);
    onZonesUpdate && onZonesUpdate(next);
    setHighlightedZone(clone);
    setSelectedZone(clone);
    setZoneMode('zone-overview');
    saveToHistory(seatsRef.current, `Zone yapıştırıldı: ${clone.name}`, next);
  };

  const duplicateZone = (zone) => {
    if (!zone || zone.isStage) return;
    copyZone(zone);
    const clone = cloneZoneGeometry(zone);
    const next = [...currentZones, clone];
    setCurrentZones(next);
    onZonesUpdate && onZonesUpdate(next);
    setHighlightedZone(clone);
    setSelectedZone(clone);
    saveToHistory(seatsRef.current, `Zone çoğaltıldı: ${clone.name}`, next);
  };

  const updateZoneFields = (zoneId, patch) => {
    const next = currentZones.map((z) => (z.id === zoneId ? { ...z, ...patch } : z));
    setCurrentZones(next);
    onZonesUpdate && onZonesUpdate(next);
    if (highlightedZone?.id === zoneId) setHighlightedZone({ ...highlightedZone, ...patch });
    if (selectedZone?.id === zoneId) setSelectedZone({ ...selectedZone, ...patch });
  };

  const distributeLayoutCapacity = (rawTarget = targetCapacity) => {
    const goal = Math.max(0, Math.floor(Number(rawTarget) || 0));
    if (!goal) {
      showAlert('Önce genel koltuk sayısını gir.');
      return;
    }
    const seatZones = currentZones.filter((z) => z && !z.isStage && z.name !== 'SAHNE');
    if (!seatZones.length) {
      showAlert('Dağıtılacak zone yok. Önce zone oluştur veya şablon yükle.');
      return;
    }
    const {
      zones: next,
      gridCols: nextCols,
      gridRows: nextRows,
    } = distributeTargetCapacity(currentZones, goal, gridCols, gridRows, naming);
    setTargetCapacity(String(goal));
    if (nextCols !== gridCols) setGridCols(nextCols);
    if (nextRows !== gridRows) setGridRows(nextRows);
    setCurrentZones(next);
    onZonesUpdate && onZonesUpdate(next);
    if (highlightedZone) {
      const h = next.find((z) => z.id === highlightedZone.id);
      if (h) setHighlightedZone(h);
    }
    if (selectedZone) {
      const s = next.find((z) => z.id === selectedZone.id);
      if (s) setSelectedZone(s);
    }
    saveToHistoryRef.current(
      seatsRef.current,
      `Kapasite dağıtıldı ve zone’lar büyütüldü: ${goal}`,
      next,
    );
  };

  const getZoneMaxSlots = (zone) => countZoneSeatSlots(zone, gridCols, gridRows, naming);

  const layoutMaxSlots = sumZoneMaxSlots(currentZones, gridCols, gridRows, naming);
  const layoutAssignedCapacity = sumZoneCapacities(currentZones);

  const switchToZoneMode = () => {
    setZoneMode('zone-overview');
    setAddSeatMode(false);
    setSelectedZone(null);
  };

  const switchToSeatMode = (zone = null) => {
    setZoneMode('seat-design');
    if (zone) {
      setSelectedZone(zone);
    } else if (useZones && currentZones.length > 0) {
      // If no specific zone provided but zones exist, select first zone
      setSelectedZone(currentZones[0]);
    }
  };

  const exitZone = () => {
    if (useZones) {
      setZoneMode('zone-overview');
      setSelectedZone(null);
      setAddSeatMode(false);
      setSelectedSeats([]);
      setShowAllZoneSeats(false);
    }
  };

  // Custom hooks - include zones in history
  const { history, historyIndex, saveToHistory, undo, redo, goToHistoryState } = useHistory(
    existingSeats,
    (newSeats) => {
      setSeats(newSeats);
      onSeatsCreate(newSeats);
    },
    zones,
    (newZones) => {
      setCurrentZones(newZones);
      onZonesUpdate && onZonesUpdate(newZones);
    },
  );
  saveToHistoryRef.current = saveToHistory;

  const getSeatAtPositionCB = (row, col) => getSeatAtPositionWithDetached(seats, row, col);

  const {
    selectedSeats,
    setSelectedSeats,
    isSelecting,
    setIsSelecting,
    selectionStart,
    selectionEnd,
    selectionBox,
    emptyAreaSelection,
    setEmptyAreaSelection,
    isSelectingEmptyArea,
    setIsSelectingEmptyArea,
    getEmptyPositionsInBox,
    toggleSeatSelection,
    clearSelection,
    startSelection,
    updateSelection,
    finishSelection,
  } = useSelection(seats, getSeatAtPositionCB, gridRows, gridCols);

  const {
    addSeat,
    paintSeats,
    eraseSeatsAt,
    removeSeat,
    moveSelectedSeats: moveSeatsUtil,
    clearSelectedSeats: clearSeatsUtil,
    clearAllSeats,
    fillEmptyAreaWithSeats: fillAreaUtil,
    fillZoneWithSeats,
    fillZoneTribune,
  } = useSeatManagement(
    seats,
    setSeats,
    onSeatsCreate,
    saveToHistory,
    getSeatAtPositionCB,
    GRID_SIZE,
    gridRows,
    gridCols,
    selectedZone,
    currentZones,
    naming,
  );

  const applyLayoutTemplate = async (templateId, opts = {}) => {
    const { confirmReplace = true, ...buildOpts } = opts;
    if (confirmReplace && currentZones.length > 0) {
      const ok = await showConfirm(
        'Mevcut zone’lar şablonla değiştirilecek. Devam?',
        'Şablon uygula',
        { confirmLabel: 'Değiştir' },
      );
      if (!ok) return;
    }
    const generated = buildTemplateZones(templateId, {
      gridCols,
      gridRows,
      ...buildOpts,
    });
    setCurrentZones(generated);
    onZonesUpdate && onZonesUpdate(generated);
    setSeats([]);
    onSeatsCreate([]);
    setSelectedZone(null);
    setHighlightedZone(null);
    setZoneMode(useZones || generated.length > 0 ? 'zone-overview' : 'seat-design');
    setActiveTemplateId(templateId);
    setActiveTool('zone-select');
    setAddSeatMode(false);
    saveToHistory([], `Şablon uygulandı: ${templateId}`, generated);
  };

  const commitNaming = (nextNaming, seatsList, historyMsg) => {
    namingRef.current = nextNaming;
    setNaming(nextNaming);
    onNamingChange && onNamingChange(nextNaming);
    const relabeled = relabelSeats(seatsList, nextNaming);
    setSeats(relabeled);
    onSeatsCreate(relabeled);
    saveToHistory(relabeled, historyMsg || 'Numaralandirma guncellendi', currentZones);
  };

  const applyNaming = (nextNaming, historyMsg) => {
    commitNaming(nextNaming, seatsRef.current, historyMsg);
  };

  const emptyColumn = (colIndex) => {
    const col = Number(colIndex);
    if (!Number.isFinite(col) || col < 0) return;
    const nextNaming = {
      ...naming,
      aisleCols: naming.aisleCols.includes(col)
        ? naming.aisleCols
        : [...naming.aisleCols, col].sort((a, b) => a - b),
      voidCols: naming.voidCols.filter((c) => c !== col),
      aisleCells: (naming.aisleCells || []).filter((key) => {
        const parts = String(key).split('-');
        return Number(parts[1]) !== col;
      }),
    };
    const cleaned = seatsRef.current.filter((s) => s.col !== col);
    commitNaming(nextNaming, cleaned, `Kolon ${col + 1} bosaltildi (koridor)`);
  };

  const voidColumn = (colIndex) => {
    const col = Number(colIndex);
    if (!Number.isFinite(col) || col < 0) return;
    const becomingVoid = !naming.voidCols.includes(col);
    const nextNaming = {
      ...naming,
      voidCols: toggleInList(naming.voidCols, col),
      aisleCols: naming.aisleCols.filter((c) => c !== col),
      aisleCells: becomingVoid
        ? (naming.aisleCells || []).filter((key) => {
            const parts = String(key).split('-');
            return Number(parts[1]) !== col;
          })
        : naming.aisleCells || [],
    };
    const cleaned = becomingVoid ? seatsRef.current.filter((s) => s.col !== col) : seatsRef.current;
    commitNaming(
      nextNaming,
      cleaned,
      becomingVoid ? `Kolon ${col + 1} gecersiz kilindi` : `Kolon ${col + 1} geri acildi`,
    );
  };

  const voidRow = (rowIndex) => {
    const row = Number(rowIndex);
    if (!Number.isFinite(row) || row < 0) return;
    const becomingVoid = !naming.voidRows.includes(row);
    const nextNaming = {
      ...naming,
      voidRows: toggleInList(naming.voidRows, row),
    };
    const cleaned = becomingVoid ? seatsRef.current.filter((s) => s.row !== row) : seatsRef.current;
    commitNaming(
      nextNaming,
      cleaned,
      becomingVoid ? `Satir ${row + 1} gecersiz kilindi` : `Satir ${row + 1} geri acildi`,
    );
  };

  const emptySelectedArea = (keysOverride = null) => {
    const keys = keysOverride || selectedSeats;
    if (!keys.length) return;
    clearSeatsUtil(keys);
    setSelectedSeats([]);
  };

  /** Remove only selected seats and mark those cells as corridor (not the whole column). */
  const emptySelectedAsAisle = (keysOverride = null) => {
    const keys = keysOverride || selectedSeats;
    if (!keys.length) return;
    const keySet = new Set(
      keys.map((k) => {
        const [r, c] = String(k).split('-');
        return `${Math.round(Number(r))}-${Math.round(Number(c))}`;
      }),
    );
    const cleaned = seatsRef.current.filter((seat) => {
      const key = seat.isDetached
        ? `${seat.row}-${seat.col}`
        : `${Math.round(seat.row)}-${Math.round(seat.col)}`;
      return !keySet.has(key);
    });
    const nextCells = [...new Set([...(naming.aisleCells || []), ...keySet])];
    const nextNaming = {
      ...naming,
      aisleCells: nextCells,
    };
    setSelectedSeats([]);
    commitNaming(nextNaming, cleaned, `Seçili hücreler boş bırakıldı (koridor)`);
  };

  const emptySeatAt = (row, col) => {
    const key = `${Math.round(row)}-${Math.round(col)}`;
    clearSeatsUtil([key]);
    setSelectedSeats((prev) => prev.filter((k) => k !== key));
  };

  const emptySeatAtAsAisle = (row, col) => {
    emptySelectedAsAisle([`${Math.round(row)}-${Math.round(col)}`]);
  };

  const restoreColumn = (colIndex) => {
    const col = Number(colIndex);
    const nextNaming = {
      ...naming,
      aisleCols: naming.aisleCols.filter((c) => c !== col),
      voidCols: naming.voidCols.filter((c) => c !== col),
      aisleCells: (naming.aisleCells || []).filter((key) => {
        const parts = String(key).split('-');
        return Number(parts[1]) !== col;
      }),
    };
    commitNaming(nextNaming, seatsRef.current, `Kolon ${col + 1} geri acildi`);
  };

  const clearAisleCellIfNeeded = (row, col) => {
    const key = `${Math.round(row)}-${Math.round(col)}`;
    const current = namingRef.current;
    if (!(current.aisleCells || []).includes(key)) return current;
    const nextNaming = {
      ...current,
      aisleCells: current.aisleCells.filter((k) => k !== key),
    };
    namingRef.current = nextNaming;
    setNaming(nextNaming);
    onNamingChange && onNamingChange(nextNaming);
    return nextNaming;
  };

  const applySeatTool = (tool) => {
    if (
      useZones &&
      zoneMode === 'zone-overview' &&
      (highlightedZone || selectedZone) &&
      !(highlightedZone || selectedZone).isStage
    ) {
      enterZone(highlightedZone || selectedZone);
    }
    setSeatTool(tool);
    if (tool === 'paint' || tool === 'erase' || tool === 'box') {
      setActiveTool('add');
      setAddSeatMode(true);
      clearSelection();
    } else {
      setActiveTool('select');
      setAddSeatMode(false);
    }
  };

  const getLiveSelectedZone = () => {
    if (!selectedZone) return null;
    return currentZones.find((z) => z.id === selectedZone.id) || selectedZone;
  };

  const canPaintAt = (pos) => {
    if (!pos) return false;
    // Zone edit: free paint anywhere on grid (tagged with selected zone).
    // Capacity / shape "sığar" is planning info only — not a hard add limit.
    if (useZones && zoneMode === 'zone-edit') {
      return !!getLiveSelectedZone();
    }
    return zoneMode === 'seat-design' || zoneMode === 'zone-edit' || !useZones;
  };

  const flushPaintBuffer = () => {
    const buf = paintBufferRef.current;
    const count = buf.length;
    paintBufferRef.current = [];
    if (!count) return;
    const relabeled = relabelSeats(seatsRef.current, naming);
    setSeats(relabeled);
    onSeatsCreate(relabeled);
    saveToHistory(
      relabeled,
      seatTool === 'erase' ? `${count} koltuk silindi` : `${count} koltuk boyandi`,
      currentZones,
    );
  };

  const bufferPaintCell = (pos) => {
    if (!canPaintAt(pos)) return;
    if (isSkippedCol(pos.col, naming) || isSkippedRow(pos.row, naming)) return;
    const key = `${pos.row}-${pos.col}`;
    if (paintBufferRef.current.some((p) => `${p.row}-${p.col}` === key)) return;
    if (seatTool === 'erase') {
      if (!getSeatAtPositionCB(pos.row, pos.col)) return;
      paintBufferRef.current.push(pos);
      setSeats((prev) => {
        const next = prev.filter((s) => !(s.row === pos.row && s.col === pos.col));
        onSeatsCreate(next);
        return next;
      });
      return;
    }
    if (getSeatAtPositionCB(pos.row, pos.col)) return;
    if (isPositionOccupied(seats, pos.row, pos.col)) return;
    const namingNow = clearAisleCellIfNeeded(pos.row, pos.col);
    paintBufferRef.current.push(pos);
    setSeats((prev) => {
      if (prev.some((s) => s.row === pos.row && s.col === pos.col)) return prev;
      const seat = {
        id: `paint-${pos.row}-${pos.col}-${Date.now()}`,
        row: pos.row,
        col: pos.col,
        x: pos.col * GRID_SIZE,
        y: pos.row * GRID_SIZE,
        label: formatSeatLabel(pos.row, pos.col, namingNow),
        seatType: selectedSeatType,
        number: getSeatNumberForCol(pos.col, namingNow, pos.row),
        width: 1,
        height: 1,
        rotation: 0,
        accessibility: selectedSeatType === 'WHEELCHAIR',
        isActive: true,
        zoneId: selectedZone?.id || null,
        _paintPreview: true,
      };
      const next = [...prev, seat];
      onSeatsCreate(next);
      return next;
    });
  };

  const { zoom, viewOffset, setViewOffset, handleZoom, handleWheel, resetZoom, zoomIn, zoomOut } =
    useZoom(gridRef);

  // When entering a zone page, frame that zone in the viewport (no outline drawn)
  useEffect(() => {
    if (zoneMode !== 'zone-edit' || !selectedZone || !containerRef.current) return;
    const z = currentZones.find((x) => x.id === selectedZone.id) || selectedZone;
    const zx = Number(z.x) || 0;
    const zy = Number(z.y) || 0;
    const zw = Math.max(Number(z.width) || 200, 80);
    const zh = Math.max(Number(z.height) || 200, 80);
    const rect = containerRef.current.getBoundingClientRect();
    const cx = zx + zw / 2;
    const cy = zy + zh / 2;
    setViewOffset({
      x: rect.width / 2 - cx * zoom - 32,
      y: rect.height / 2 - cy * zoom - 48,
    });
    // Only re-frame when switching zone / entering edit
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoneMode, selectedZone?.id]);

  // Wrapper functions that also update selectedSeats state
  const moveSelectedSeats = (deltaRow, deltaCol) => {
    // YersizleÅŸtirilmiÅŸ koltuklar iÃ§in Ã¶zel hareket
    const selectedSeatObjects = seats.filter((seat) => {
      const seatKey = seat.isDetached
        ? `${seat.row}-${seat.col}`
        : `${Math.round(seat.row)}-${Math.round(seat.col)}`;
      return selectedSeats.includes(seatKey) && seat.isDetached;
    });

    if (selectedSeatObjects.length > 0) {
      // YersizleÅŸtirilmiÅŸ koltuklar iÃ§in direkt gÃ¼ncelleme
      const updatedSeats = seats.map((seat) => {
        const seatKey = seat.isDetached
          ? `${seat.row}-${seat.col}`
          : `${Math.round(seat.row)}-${Math.round(seat.col)}`;
        if (selectedSeats.includes(seatKey) && seat.isDetached) {
          const newRow = seat.row + deltaRow * 0.5; // Daha hassas hareket
          const newCol = seat.col + deltaCol * 0.5;

          // SÄ±nÄ±r kontrolÃ¼
          if (newRow < 0 || newRow >= gridRows || newCol < 0 || newCol >= gridCols) {
            return seat;
          }

          return {
            ...seat,
            row: newRow,
            col: newCol,
            x: newCol * GRID_SIZE,
            y: newRow * GRID_SIZE,
            label: formatSeatLabel(Math.floor(newRow), Math.floor(newCol), naming),
          };
        }
        return seat;
      });

      setSeats(updatedSeats);
      onSeatsCreate(updatedSeats);
      saveToHistory(updatedSeats, 'Koltuklar taşındı', currentZones);

      // selectedSeats'i gÃ¼ncelle (yeni pozisyonlar)
      const newSelectedSeats = updatedSeats
        .filter(
          (seat) =>
            seat.isDetached && selectedSeatObjects.some((origSeat) => origSeat.id === seat.id),
        )
        .map((seat) => `${seat.row}-${seat.col}`);

      setSelectedSeats(newSelectedSeats);
    } else {
      // Normal hareket iÃ§in orijinal fonksiyon
      const newSelectedSeats = moveSeatsUtil(selectedSeats, deltaRow, deltaCol);
      if (newSelectedSeats) {
        setSelectedSeats(newSelectedSeats);
      }
    }
  };

  const clearSelectedSeats = () => {
    clearSeatsUtil(selectedSeats);
    setSelectedSeats([]);
  };

  // Ghost preview gÃ¼ncelleme fonksiyonu
  const updateGhostPreview = (clientX, clientY) => {
    if (!positioningMode || positioningSeats.length === 0) {
      setGhostPreviewSeats([]);
      return;
    }

    const canvas = clientToCanvasCoords(clientX, clientY, gridRef, zoom);
    if (!canvas) return;

    const exactCol = canvas.x / GRID_SIZE;
    const exactRow = canvas.y / GRID_SIZE;

    const targetCol = Math.floor(Math.max(0, Math.min(gridCols - 1, exactCol)));
    const targetRow = Math.floor(Math.max(0, Math.min(gridRows - 1, exactRow)));

    // SeÃ§ili koltuklarÄ±n mevcut pozisyonlarÄ±nÄ± al
    const positioningSeatsData = seats.filter((seat) =>
      positioningSeats.includes(`${seat.row}-${seat.col}`),
    );

    if (positioningSeatsData.length === 0) return;

    // Grup iÃ§in referans noktasÄ± (ilk koltuk)
    const referenceSeat = positioningSeatsData[0];
    const deltaRow = targetRow - referenceSeat.row;
    const deltaCol = targetCol - referenceSeat.col;

    // TÃ¼m koltuklar iÃ§in ghost pozisyonlarÄ± hesapla
    const ghostPositions = positioningSeatsData.map((seat) => ({
      row: seat.row + deltaRow,
      col: seat.col + deltaCol,
      seatType: seat.seatType,
      id: `ghost-${seat.id}`,
    }));

    // Collision check - hangi pozisyonlar geÃ§erli?
    const validGhosts = ghostPositions.filter(({ row, col }) => {
      // Grid sÄ±nÄ±rlarÄ± iÃ§inde mi?
      if (row < 0 || row >= gridRows || col < 0 || col >= gridCols) {
        return false;
      }

      // Hedef pozisyonda baÅŸka koltuk var mÄ±?
      const existingSeat = seats.some((seat) => {
        const seatKey = `${seat.row}-${seat.col}`;
        return seat.row === row && seat.col === col && !positioningSeats.includes(seatKey);
      });

      return !existingSeat;
    });

    setGhostPreviewSeats(validGhosts);
  };

  const positionGroupAt = (clientX, clientY) => {
    const canvas = clientToCanvasCoords(clientX, clientY, gridRef, zoom);
    if (!canvas) return;

    const exactCol = canvas.x / GRID_SIZE;
    const exactRow = canvas.y / GRID_SIZE;

    const targetCol = Math.floor(Math.max(0, Math.min(gridCols - 1, exactCol)));
    const targetRow = Math.floor(Math.max(0, Math.min(gridRows - 1, exactRow)));

    // SeÃ§ili koltuklarÄ±n mevcut pozisyonlarÄ±nÄ± al
    const positioningSeatsData = seats.filter((seat) =>
      positioningSeats.includes(`${seat.row}-${seat.col}`),
    );

    if (positioningSeatsData.length === 0) return;

    // Grup iÃ§in referans noktasÄ± (ilk koltuk)
    const referenceSeat = positioningSeatsData[0];
    const deltaRow = targetRow - referenceSeat.row;
    const deltaCol = targetCol - referenceSeat.col;

    // Collision check
    const canPlace = positioningSeatsData.every((seat) => {
      const newRow = seat.row + deltaRow;
      const newCol = seat.col + deltaCol;

      // Grid sÄ±nÄ±rlarÄ± iÃ§inde mi?
      if (newRow < 0 || newRow >= gridRows || newCol < 0 || newCol >= gridCols) {
        return false;
      }

      // Hedef pozisyonda baÅŸka koltuk var mÄ±?
      const existingSeat = seats.some((s) => {
        const sKey = `${s.row}-${s.col}`;
        return s.row === newRow && s.col === newCol && !positioningSeats.includes(sKey);
      });

      return !existingSeat;
    });

    if (!canPlace) {
      setPositioningError('Bu pozisyona yerleÅŸtirilemez!');
      setTimeout(() => setPositioningError(null), 2000);
      return;
    }

    // KoltuklarÄ± yeni pozisyonlara taÅŸÄ±
    const updatedSeats = seats.map((seat) => {
      const seatKey = `${seat.row}-${seat.col}`;
      if (positioningSeats.includes(seatKey)) {
        return {
          ...seat,
          row: seat.row + deltaRow,
          col: seat.col + deltaCol,
        };
      }
      return seat;
    });

    setSeats(updatedSeats);
    onSeatsCreate(updatedSeats);
    saveToHistory(updatedSeats, `${positioningSeats.length} koltuk taÅŸÄ±ndÄ±`);

    // Positioning modunu kapat
    setPositioningMode(false);
    setPositioningSeats([]);
    setGhostPreviewSeats([]);
    setPositioningError(null);

    // Yeni pozisyonlarda koltuklarÄ± seÃ§ili tut
    const newSelectedSeats = positioningSeatsData.map((seat) => {
      const newRow = seat.row + deltaRow;
      const newCol = seat.col + deltaCol;
      return `${newRow}-${newCol}`;
    });
    setSelectedSeats(newSelectedSeats);
  };

  // Theater layout handler
  const addTheaterLayoutHandler = () => {
    addTheaterLayout(
      seats,
      setSeats,
      onSeatsCreate,
      saveToHistory,
      getSeatAtPositionCB,
      gridCols,
      selectedZone,
      gridRows,
    );
  };

  // Row adding handler
  const addRowFromPositionHandler = (row, col) => {
    addRowFromPosition(
      seats,
      setSeats,
      onSeatsCreate,
      saveToHistory,
      getSeatAtPositionCB,
      row,
      col,
      10, // default seats count
      gridCols,
      selectedZone,
      gridRows,
    );
  };

  // Fill empty area handler
  const fillEmptyAreaWithSeats = (selectionBox) => {
    fillAreaUtil(getEmptyPositionsInBox, selectionBox);
    setEmptyAreaSelection(null);
  };

  const changeSelectedSeatType = (seatType, keysOverride = null) => {
    const keys = keysOverride || selectedSeats;
    if (!keys.length) return;
    const updated = seats.map((seat) => {
      const key = seat.isDetached
        ? `${seat.row}-${seat.col}`
        : `${Math.round(seat.row)}-${Math.round(seat.col)}`;
      if (!keys.includes(key)) return seat;
      return {
        ...seat,
        seatType,
        accessibility: seatType === 'WHEELCHAIR',
      };
    });
    setSeats(updated);
    onSeatsCreate(updated);
    saveToHistory(updated, `Koltuk tipi → ${seatType}`, currentZones);
  };

  const renameSeats = (keysOverride = null, nextLabel = null) => {
    const keys = keysOverride || selectedSeats;
    if (!keys.length) return;
    const orderedKeys = [...keys].sort((a, b) => {
      const [ar, ac] = a.split('-').map(Number);
      const [br, bc] = b.split('-').map(Number);
      return ar !== br ? ar - br : ac - bc;
    });
    const current = seats.find((seat) => orderedKeys.includes(seatKeyOf(seat)));
    const suggested =
      nextLabel != null
        ? nextLabel
        : current?.label ||
          formatSeatLabel(Math.round(current?.row || 0), Math.round(current?.col || 0), naming);
    setRenameModal({ keys: orderedKeys, suggestedLabel: suggested });
  };

  const applyRenameSeats = ({ labels }) => {
    if (!renameModal?.keys?.length || !labels?.length) {
      setRenameModal(null);
      return;
    }
    const keys = renameModal.keys;
    const taken = new Set(
      seats.filter((seat) => !keys.includes(seatKeyOf(seat))).map((s) => String(s.label || '')),
    );
    const labelByKey = new Map();
    keys.forEach((key, i) => {
      let label = String(labels[i] || labels[labels.length - 1] || '')
        .trim()
        .slice(0, 20);
      let bump = 0;
      let candidate = label;
      while (taken.has(candidate) || [...labelByKey.values()].includes(candidate)) {
        bump += 1;
        candidate = `${label}${bump}`.slice(0, 20);
      }
      labelByKey.set(key, candidate);
    });
    const updated = seats.map((seat) => {
      const key = seatKeyOf(seat);
      if (!labelByKey.has(key)) return seat;
      const label = labelByKey.get(key);
      return {
        ...seat,
        label,
        customLabel: true,
        metadata: { ...(seat.metadata || {}), customLabel: true },
      };
    });
    setSeats(updated);
    onSeatsCreate(updated);
    saveToHistory(updated, `Koltuk ismi: ${labels[0]}`, currentZones);
    setRenameModal(null);
  };

  const clearCustomSeatLabels = (keysOverride = null) => {
    const keys = keysOverride || selectedSeats;
    if (!keys.length) return;
    const updated = relabelSeats(
      seats.map((seat) => {
        const key = seat.isDetached
          ? `${seat.row}-${seat.col}`
          : `${Math.round(seat.row)}-${Math.round(seat.col)}`;
        if (!keys.includes(key)) return seat;
        const { customLabel, ...rest } = seat;
        return {
          ...rest,
          customLabel: false,
          metadata: { ...(seat.metadata || {}), customLabel: false },
        };
      }),
      naming,
    );
    setSeats(updated);
    onSeatsCreate(updated);
    saveToHistory(updated, 'Otomatik isimlendirmeye dönüldü', currentZones);
  };

  const duplicateSelectedSeats = (direction = 'right', keysOverride = null) => {
    const keys = keysOverride || selectedSeats;
    if (!keys.length) return;
    const selected = seats.filter((seat) => {
      const key = seat.isDetached
        ? `${seat.row}-${seat.col}`
        : `${Math.round(seat.row)}-${Math.round(seat.col)}`;
      return keys.includes(key);
    });
    if (!selected.length) return;

    const rows = selected.map((s) => Math.round(s.row));
    const cols = selected.map((s) => Math.round(s.col));
    const minRow = Math.min(...rows);
    const maxRow = Math.max(...rows);
    const minCol = Math.min(...cols);
    const maxCol = Math.max(...cols);
    // Place the whole block next to itself (not diagonal)
    const dRow = direction === 'down' ? maxRow - minRow + 1 : 0;
    const dCol = direction === 'right' ? maxCol - minCol + 1 : 0;

    const clones = [];
    const newKeys = [];
    selected.forEach((seat) => {
      const row = Math.round(seat.row) + dRow;
      const col = Math.round(seat.col) + dCol;
      if (row < 0 || row >= gridRows || col < 0 || col >= gridCols) return;
      if (isSkippedCol(col, naming) || isSkippedRow(row, naming)) return;
      if (isPositionOccupied([...seats, ...clones], row, col)) return;
      clones.push({
        ...seat,
        id: `seat-${Date.now()}-${row}-${col}-${Math.random()}`,
        row,
        col,
        x: col * GRID_SIZE,
        y: row * GRID_SIZE,
        isDetached: false,
        // Clones get auto labels from grid position
        customLabel: false,
        label: formatSeatLabel(row, col, naming),
        number: getSeatNumberForCol(col, naming, row),
        zoneId: selectedZone?.id || seat.zoneId || null,
        metadata: { ...(seat.metadata || {}), customLabel: false },
      });
      newKeys.push(`${row}-${col}`);
    });
    if (!clones.length) return;
    // Keep grid naming: right → B1,B2,B3…  down → B1,C1,D1…
    const updated = relabelSeats([...seats, ...clones], naming);
    setSeats(updated);
    onSeatsCreate(updated);
    setSelectedSeats(newKeys);
    saveToHistory(
      updated,
      direction === 'down'
        ? `${clones.length} koltuk aşağı çoğaltıldı`
        : `${clones.length} koltuk sağa çoğaltıldı`,
      currentZones,
    );
  };

  const handleBackgroundChange = (dataUrl) => {
    setBackgroundImage(dataUrl);
    if (typeof onBackgroundChange === 'function') onBackgroundChange(dataUrl);
  };

  // History handlers
  const undoHandler = () => {
    undo();
    setSelectedSeats([]);
  };

  const redoHandler = () => {
    redo();
    setSelectedSeats([]);
  };

  const goToHistoryStateHandler = (index) => {
    goToHistoryState(index);
    setSelectedSeats([]);
  };

  // Positioning mode handlers
  const enablePositioningMode = () => {
    if (selectedSeats.length === 0) return;

    setPositioningMode(true);
    setPositioningSeats([...selectedSeats]);
    setPositioningError(null);
  };

  const disablePositioningMode = () => {
    setPositioningMode(false);
    setPositioningSeats([]);
    setGhostPreviewSeats([]);
    setPositioningError(null);
  };

  // Detached mode handlers
  const enableSingleSeatPrecisionMode = () => {
    if (selectedSeats.length !== 1) return;

    const seatKey = selectedSeats[0];
    const seat = seats.find((s) => `${s.row}-${s.col}` === seatKey);
    if (!seat) return;

    const updatedSeats = seats.map((s) => (s.id === seat.id ? { ...s, isDetached: true } : s));

    setSeats(updatedSeats);
    onSeatsCreate(updatedSeats);
    saveToHistory(updatedSeats, 'Koltuk yersizleÅŸtirildi');
  };

  const disableDetachedMode = () => {
    const updatedSeats = seats.map((seat) => {
      const seatKey = seat.isDetached
        ? `${seat.row}-${seat.col}`
        : `${Math.round(seat.row)}-${Math.round(seat.col)}`;
      if (selectedSeats.includes(seatKey) && seat.isDetached) {
        const newRow = Math.round(seat.row);
        const newCol = Math.round(seat.col);
        return {
          ...seat,
          isDetached: false,
          row: newRow,
          col: newCol,
          x: newCol * GRID_SIZE,
          y: newRow * GRID_SIZE,
          label: formatSeatLabel(newRow, newCol, naming),
          number: getSeatNumberForCol(newCol, naming, newRow),
        };
      }
      return seat;
    });

    setSeats(updatedSeats);
    onSeatsCreate(updatedSeats);
    saveToHistory(updatedSeats, "Koltuklar grid'e hizalandı", currentZones);

    // selectedSeats'i yeni pozisyonlarla gÃ¼ncelle
    const newSelectedSeats = selectedSeats.map((seatKey) => {
      const seat = seats.find((s) => {
        const key = s.isDetached
          ? `${s.row}-${s.col}`
          : `${Math.round(s.row)}-${Math.round(s.col)}`;
        return key === seatKey;
      });
      if (seat && seat.isDetached) {
        return `${Math.round(seat.row)}-${Math.round(seat.col)}`;
      }
      return seatKey;
    });

    setSelectedSeats(newSelectedSeats);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // Mouse event handlers
  const handleMouseDown = (e) => {
    e.preventDefault();
    setContextMenu(null);

    // Zone Overview Mode - Only zone operations allowed
    if (useZones && zoneMode === 'zone-overview') {
      // Check if clicking on a zone first (only if not in polygon mode or not actively drawing)
      if (zoneDrawingMode !== 'polygon' || !isPolygonMode) {
        const canvas = clientToCanvasCoords(e.clientX, e.clientY, gridRef, zoom);
        if (canvas) {
          const { x, y } = canvas;

          // Check if clicking on existing zone
          const clickedZone = currentZones.find(
            (zone) =>
              x >= zone.x && x <= zone.x + zone.width && y >= zone.y && y <= zone.y + zone.height,
          );

          if (clickedZone) {
            handleZoneClick(clickedZone, e.clientX, e.clientY);
            return;
          }
        }
      }

      // Handle zone creation (including polygon mode)
      if (isCreatingZone) {
        handleZoneCanvasMouseDown(e);
        return;
      }

      // Block all seat operations in zone overview mode
      return;
    }

    // Zone Edit Mode — need a selected zone; seats can be placed freely (any count)
    if (useZones && zoneMode === 'zone-edit') {
      if (!getLiveSelectedZone()) return;
    }

    const pos = getGridPosition(
      e.clientX,
      e.clientY,
      gridRef,
      zoom,
      viewOffset,
      gridCols,
      gridRows,
    );
    if (!pos) return;

    // Ã–nce normal koltuk ara, sonra yersizleÅŸtirilmiÅŸ koltuklarÄ± da kontrol et
    const existingSeat =
      getSeatAtPositionCB(pos.row, pos.col) ||
      seats.find((seat) => {
        if (!seat.isDetached) return false;
        // YersizleÅŸtirilmiÅŸ koltuk iÃ§in proximity check
        const minRow = seat.row - 0.5;
        const maxRow = seat.row + 0.5;
        const minCol = seat.col - 0.5;
        const maxCol = seat.col + 0.5;
        return pos.row >= minRow && pos.row < maxRow && pos.col >= minCol && pos.col < maxCol;
      });

    if (positioningMode) {
      if (positioningSeats.length > 0) {
        positionGroupAt(e.clientX, e.clientY);
      }
      return;
    }

    // Ticket-site tools: paint / erase / box
    if (
      (seatTool === 'paint' || seatTool === 'erase') &&
      (zoneMode === 'seat-design' || zoneMode === 'zone-edit' || !useZones)
    ) {
      paintBufferRef.current = [];
      bufferPaintCell(pos);
      setIsDrawing(true);
      return;
    }

    if (
      seatTool === 'box' &&
      (zoneMode === 'seat-design' || zoneMode === 'zone-edit' || !useZones)
    ) {
      setBoxStart(pos);
      setBoxEnd(pos);
      setIsDrawing(true);
      return;
    }

    if (addSeatMode && (zoneMode === 'seat-design' || zoneMode === 'zone-edit')) {
      if (!existingSeat) {
        addSeat(pos.row, pos.col, selectedSeatType);
        setIsDrawing(true);
      }
      return;
    }

    if (existingSeat) {
      const seatKey = existingSeat.isDetached
        ? `${existingSeat.row}-${existingSeat.col}`
        : `${Math.round(existingSeat.row)}-${Math.round(existingSeat.col)}`;

      if (e.shiftKey || e.ctrlKey || e.metaKey) {
        toggleSeatSelection(existingSeat.row, existingSeat.col);
      } else {
        if (!selectedSeats.includes(seatKey)) {
          setSelectedSeats([seatKey]);
        }
        setIsDragging(true);
        setDragStartPos({ row: existingSeat.row, col: existingSeat.col });
      }
    } else {
      if (!e.shiftKey && !e.ctrlKey && !e.metaKey) {
        clearSelection();
      }
      startSelection(pos);
    }
  };

  const handleMouseMove = (e) => {
    e.preventDefault();

    if (isDraggingZone) {
      handleZoneMouseMove(e);
      return;
    }

    if (useZones && zoneMode === 'zone-overview') {
      handleZoneCanvasMouseMove(e);
      return;
    }

    const pos = getGridPosition(
      e.clientX,
      e.clientY,
      gridRef,
      zoom,
      viewOffset,
      gridCols,
      gridRows,
    );
    if (pos) {
      setHoverCell(pos);
      updateSelection(pos);
    } else {
      setHoverCell(null);
    }

    if (positioningMode && positioningSeats.length > 0) {
      updateGhostPreview(e.clientX, e.clientY);
    }

    if (isDrawing && seatTool === 'box' && boxStart && pos) {
      setBoxEnd(pos);
      return;
    }

    if (isDrawing && (seatTool === 'paint' || seatTool === 'erase') && pos) {
      bufferPaintCell(pos);
      return;
    }

    if (isDrawing && addSeatMode && seatTool !== 'box') {
      if (pos && !getSeatAtPositionCB(pos.row, pos.col) && canPaintAt(pos)) {
        addSeat(pos.row, pos.col, selectedSeatType, { recordHistory: false });
      }
    }

    if (isDragging && dragStartPos && selectedSeats.length > 0 && pos) {
      const deltaRow = pos.row - dragStartPos.row;
      const deltaCol = pos.col - dragStartPos.col;
      if (Math.abs(deltaRow) >= 1 || Math.abs(deltaCol) >= 1) {
        moveSelectedSeats(deltaRow, deltaCol);
        setDragStartPos({ row: pos.row, col: pos.col });
      }
    }
  };

  const handleMouseUp = (e) => {
    e.preventDefault();

    if (isDraggingZone) {
      handleZoneMouseUp();
      return;
    }

    if (useZones && zoneMode === 'zone-overview') {
      handleZoneCanvasMouseUp(e);
      return;
    }

    if (isDrawing && seatTool === 'box' && boxStart && boxEnd) {
      const minRow = Math.min(boxStart.row, boxEnd.row);
      const maxRow = Math.max(boxStart.row, boxEnd.row);
      const minCol = Math.min(boxStart.col, boxEnd.col);
      const maxCol = Math.max(boxStart.col, boxEnd.col);
      const cells = [];
      for (let r = minRow; r <= maxRow; r += 1) {
        for (let c = minCol; c <= maxCol; c += 1) {
          const cell = { row: r, col: c };
          if (canPaintAt(cell)) cells.push(cell);
        }
      }
      paintSeats(cells, selectedSeatType);
      setBoxStart(null);
      setBoxEnd(null);
      setIsDrawing(false);
      return;
    }

    if (isDrawing && (seatTool === 'paint' || seatTool === 'erase')) {
      flushPaintBuffer();
      setIsDrawing(false);
      return;
    }

    finishSelection(e);
    setIsDrawing(false);
    setIsDragging(false);
    setDragStartPos(null);
    setBoxStart(null);
    setBoxEnd(null);
  };

  const handleContextMenu = (e) => {
    e.preventDefault();

    // Zone Overview Mode - Only zone context menus allowed
    if (useZones && zoneMode === 'zone-overview') {
      const canvas = clientToCanvasCoords(e.clientX, e.clientY, gridRef, zoom);
      if (canvas) {
        const { x, y } = canvas;

        // Check if right-clicking on existing zone
        const clickedZone = currentZones.find(
          (zone) =>
            x >= zone.x && x <= zone.x + zone.width && y >= zone.y && y <= zone.y + zone.height,
        );

        if (clickedZone) {
          setContextMenu({
            x: e.clientX,
            y: e.clientY,
            visible: true,
            type: 'zone-overview',
            zone: clickedZone,
          });
          return;
        }

        // If not clicking on a zone, show zone creation menu
        setContextMenu({
          x: e.clientX,
          y: e.clientY,
          visible: true,
          type: 'zone-overview-empty',
        });
        return;
      }
    }

    // Zone Edit Mode — context menu anywhere while a zone is selected
    if (useZones && zoneMode === 'zone-edit') {
      if (!selectedZone) return;
    }

    const pos = getGridPosition(
      e.clientX,
      e.clientY,
      gridRef,
      zoom,
      viewOffset,
      gridCols,
      gridRows,
    );
    if (!pos) return;

    const existingSeat = getSeatAtPositionCB(pos.row, pos.col);

    if (selectedSeats.length > 0) {
      setContextMenu({
        x: e.clientX,
        y: e.clientY,
        visible: true,
        type: useZones && zoneMode === 'zone-edit' ? 'zone-edit-selectedSeats' : 'selectedSeats',
        gridPosition: pos,
        zone: selectedZone,
      });
    } else if (emptyAreaSelection) {
      setContextMenu({
        x: e.clientX,
        y: e.clientY,
        visible: true,
        type:
          useZones && zoneMode === 'zone-edit'
            ? 'zone-edit-emptyAreaSelection'
            : 'emptyAreaSelection',
        gridPosition: pos,
        selectionBox: emptyAreaSelection,
        zone: selectedZone,
      });
    } else if (!existingSeat) {
      setContextMenu({
        x: e.clientX,
        y: e.clientY,
        visible: true,
        type: useZones && zoneMode === 'zone-edit' ? 'zone-edit-emptyArea' : 'emptyArea',
        gridPosition: pos,
        zone: selectedZone,
      });
    } else {
      // Right-click on a seat (no multi-selection) — column/row + seat actions
      setContextMenu({
        x: e.clientX,
        y: e.clientY,
        visible: true,
        type: useZones && zoneMode === 'zone-edit' ? 'zone-edit-seat' : 'seat',
        gridPosition: pos,
        seat: existingSeat,
        zone: selectedZone,
      });
    }
  };

  // Keep zone drag working even if cursor leaves the grid
  useEffect(() => {
    if (!isDraggingZone) return undefined;
    const onMove = (e) => handleZoneMouseMove(e);
    const onUp = () => handleZoneMouseUp();
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDraggingZone, draggedZone, zoneDragOffset, naming]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undoHandler();
      } else if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        redoHandler();
      } else if ((e.ctrlKey || e.metaKey) && (e.key === 'c' || e.key === 'C')) {
        if (
          useZones &&
          (highlightedZone || selectedZone) &&
          !e.target?.closest?.('input,textarea,select')
        ) {
          e.preventDefault();
          copyZone(highlightedZone || selectedZone);
        }
      } else if ((e.ctrlKey || e.metaKey) && (e.key === 'v' || e.key === 'V')) {
        if (useZones && zoneClipboardRef.current && !e.target?.closest?.('input,textarea,select')) {
          e.preventDefault();
          pasteZone();
        }
      } else if ((e.ctrlKey || e.metaKey) && (e.key === 'd' || e.key === 'D')) {
        if (e.target?.closest?.('input,textarea,select')) {
          // let browser handle
        } else if (selectedSeats.length > 0) {
          e.preventDefault();
          // Shift+D = down, plain Ctrl+D = right
          duplicateSelectedSeats(e.shiftKey ? 'down' : 'right');
        } else if (useZones && (highlightedZone || selectedZone)) {
          e.preventDefault();
          duplicateZone(highlightedZone || selectedZone);
        }
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        if (
          isPolygonMode &&
          polygonPoints.length > 0 &&
          e.key === 'Backspace' &&
          selectedSeats.length === 0
        ) {
          e.preventDefault();
          setPolygonPoints((prev) => prev.slice(0, -1));
        } else if (
          useZones &&
          zoneMode === 'zone-overview' &&
          (highlightedZone || selectedZone) &&
          selectedSeats.length === 0 &&
          !e.target?.closest?.('input,textarea,select')
        ) {
          e.preventDefault();
          deleteZone((highlightedZone || selectedZone).id);
        } else if (selectedSeats.length > 0) {
          e.preventDefault();
          clearSelectedSeats();
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        if (isPolygonMode || polygonPoints.length > 0) {
          setPolygonPoints([]);
          setIsPolygonMode(false);
          setPreviewZone(null);
        } else if (positioningMode) {
          disablePositioningMode();
        } else if (contextMenu) {
          setContextMenu(null);
        } else {
          clearSelection();
        }
      } else if (e.key === 'Enter' && isPolygonMode && polygonPoints.length >= 3) {
        e.preventDefault();
        finishPolygonZone();
      } else if (e.key === 'F11') {
        e.preventDefault();
        toggleFullscreen();
      }
    };

    const handleClickOutside = (e) => {
      if (contextMenu && !e.target.closest('.context-menu')) {
        setContextMenu(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [
    selectedSeats,
    contextMenu,
    positioningMode,
    isSelecting,
    highlightedZone,
    selectedZone,
    zoneMode,
    useZones,
    currentZones,
    isPolygonMode,
    polygonPoints,
  ]);

  return (
    <div className="layouts-v2-root layouts-v2-editor-overlay overflow-hidden">
      <div className="flex flex-col h-full bg-gray-100">
        <div className="flex flex-1 min-h-0">
          <EditorSidebar
            useZones={useZones}
            zoneMode={zoneMode}
            selectedZone={selectedZone}
            currentZones={currentZones}
            seats={seats}
            isCreatingZone={isCreatingZone}
            setIsCreatingZone={setIsCreatingZone}
            newZone={newZone}
            setNewZone={setNewZone}
            selectedSeatType={selectedSeatType}
            setSelectedSeatType={setSelectedSeatType}
            setAddSeatMode={setAddSeatMode}
            addSeatMode={addSeatMode}
            fillZoneWithSeats={fillZoneWithSeats}
            fillZoneTribune={fillZoneTribune}
            exitZone={exitZone}
            enterZone={enterZone}
            onSeatsCreate={onSeatsCreate}
            setSeats={setSeats}
            saveToHistory={saveToHistory}
            zoneDrawingMode={zoneDrawingMode}
            setZoneDrawingMode={setZoneDrawingMode}
            setActiveTool={setActiveTool}
            activeTool={activeTool}
            seatTool={seatTool}
            applySeatTool={applySeatTool}
            backgroundImage={backgroundImage}
            onBackgroundImageChange={handleBackgroundChange}
            onBackgroundClear={() => handleBackgroundChange(null)}
            backgroundOpacity={backgroundOpacity}
            onBackgroundOpacityChange={setBackgroundOpacity}
            gridCols={gridCols}
            gridRows={gridRows}
            setGridCols={setGridCols}
            setGridRows={setGridRows}
            onPrevious={onPrevious}
            onNext={onNext}
            naming={naming}
            setNaming={setNaming}
            applyNaming={applyNaming}
            aisleColInput={aisleColInput}
            setAisleColInput={setAisleColInput}
            emptyColumn={emptyColumn}
            zoom={zoom}
            zoomIn={zoomIn}
            zoomOut={zoomOut}
            resetZoom={resetZoom}
            layoutType={layoutType}
            isAreaLayout={isAreaLayout}
            layoutTemplates={LAYOUT_TEMPLATES}
            activeTemplateId={activeTemplateId}
            applyLayoutTemplate={applyLayoutTemplate}
            highlightedZone={highlightedZone}
            setHighlightedZone={setHighlightedZone}
            deleteZone={deleteZone}
            copyZone={copyZone}
            pasteZone={pasteZone}
            duplicateZone={duplicateZone}
            updateZoneFields={updateZoneFields}
            zoneClipboardReady={zoneClipboardReady}
            targetCapacity={targetCapacity}
            setTargetCapacity={setTargetCapacity}
            distributeLayoutCapacity={distributeLayoutCapacity}
            getZoneMaxSlots={getZoneMaxSlots}
            layoutMaxSlots={layoutMaxSlots}
            layoutAssignedCapacity={layoutAssignedCapacity}
            showAllZoneSeats={showAllZoneSeats}
            setShowAllZoneSeats={setShowAllZoneSeats}
            showAlert={showAlert}
          />

          <div className="flex-1 min-w-0 flex flex-col">
            <Canvas
              gridRef={gridRef}
              containerRef={containerRef}
              seats={seats}
              selectedSeats={selectedSeats}
              positioningSeats={positioningSeats}
              positioningMode={positioningMode}
              addSeatMode={addSeatMode}
              zoom={zoom}
              viewOffset={viewOffset}
              isSelecting={isSelecting}
              selectionBox={selectionBox}
              emptyAreaSelection={emptyAreaSelection}
              isFullscreen={isFullscreen}
              gridCols={gridCols}
              setGridCols={setGridCols}
              gridRows={gridRows}
              setGridRows={setGridRows}
              ghostPreviewSeats={ghostPreviewSeats}
              activeTool={activeTool}
              setActiveTool={setActiveTool}
              backgroundImage={backgroundImage}
              backgroundOpacity={backgroundOpacity}
              hideToolRail
              hoverCell={hoverCell}
              seatTool={seatTool}
              boxStart={boxStart}
              boxEnd={boxEnd}
              naming={naming}
              zoomIn={zoomIn}
              zoomOut={zoomOut}
              resetZoom={resetZoom}
              zones={currentZones}
              useZones={useZones}
              selectedZone={selectedZone}
              highlightedZone={highlightedZone}
              zoneMode={zoneMode}
              isCreatingZone={isCreatingZone}
              isDrawingZone={isDrawingZone}
              previewZone={previewZone}
              zoneDrawingMode={zoneDrawingMode}
              polygonPoints={polygonPoints}
              isPolygonMode={isPolygonMode}
              isDraggingZone={isDraggingZone}
              draggedZone={draggedZone}
              handleMouseDown={handleMouseDown}
              handleMouseMove={handleMouseMove}
              handleMouseUp={handleMouseUp}
              handleContextMenu={handleContextMenu}
              handleWheel={handleWheel}
              setAddSeatMode={setAddSeatMode}
              undo={undoHandler}
              redo={redoHandler}
              historyIndex={historyIndex}
              history={history}
              zoomIn={zoomIn}
              zoomOut={zoomOut}
              resetZoom={resetZoom}
              toggleFullscreen={toggleFullscreen}
              clearSelectedSeats={clearSelectedSeats}
              setViewOffset={setViewOffset}
              onSeatsUpdate={(newSeats) => {
                setSeats(newSeats);
                onSeatsCreate(newSeats);
                saveToHistory(newSeats, 'Koltuk silindi');
              }}
              handleZoomClick={handleZoom}
              onPositionGroup={positionGroupAt}
              onZoneClick={handleZoneClick}
              onZoneDoubleClick={handleZoneDoubleClick}
              onZoneMouseDown={handleZoneMouseDown}
              onZoneCreateModeChange={setIsCreatingZone}
              onZoneExit={exitZone}
              showAllZoneSeats={showAllZoneSeats}
              onToggleShowAllZoneSeats={() => setShowAllZoneSeats((v) => !v)}
              fillEmptyAreaWithSeats={fillEmptyAreaWithSeats}
              setEmptyAreaSelection={setEmptyAreaSelection}
              onAxisContextMenu={({ kind, index, clientX, clientY }) => {
                setContextMenu({
                  x: clientX,
                  y: clientY,
                  visible: true,
                  type: kind === 'col' ? 'col-header' : 'row-header',
                  gridPosition: kind === 'col' ? { row: 0, col: index } : { row: index, col: 0 },
                });
              }}
            />
            <div className="flex-shrink-0 px-3 py-1.5 bg-white border-t border-gray-200 flex items-center justify-between text-[11px] text-gray-500">
              <span>
                Blok’a tıkla = zone sayfası · Sürükle = taşı · El / Space = gez · Sağ tık = menü
              </span>
              <span>
                {seats.length} yerleşen
                {useZones
                  ? ` · hedef ${targetCapacity || '—'} · atanan ${layoutAssignedCapacity} · max ${layoutMaxSlots}`
                  : ''}
                {' · '}
                {currentZones.length} zone · {gridCols}×{gridRows}
              </span>
            </div>
          </div>
        </div>
      </div>

      <ContextMenu
        contextMenu={contextMenu}
        selectedSeats={selectedSeats}
        seats={seats}
        selectedSeatType={selectedSeatType}
        setSelectedSeatType={setSelectedSeatType}
        enablePositioningMode={enablePositioningMode}
        enableSingleSeatPrecisionMode={enableSingleSeatPrecisionMode}
        disableDetachedMode={disableDetachedMode}
        clearSelectedSeats={clearSelectedSeats}
        addRowFromPosition={addRowFromPositionHandler}
        fillEmptyAreaWithSeats={fillEmptyAreaWithSeats}
        setEmptyAreaSelection={setEmptyAreaSelection}
        setContextMenu={setContextMenu}
        deleteZone={deleteZone}
        enterZone={enterZone}
        addSeatAt={(row, col, type) => addSeat(row, col, type || selectedSeatType)}
        fillZoneWithSeats={fillZoneWithSeats}
        fillZoneTribune={fillZoneTribune}
        setAddSeatMode={setAddSeatMode}
        setActiveTool={setActiveTool}
        changeSelectedSeatType={changeSelectedSeatType}
        renameSeats={renameSeats}
        clearCustomSeatLabels={clearCustomSeatLabels}
        duplicateSelectedSeats={duplicateSelectedSeats}
        emptyColumn={emptyColumn}
        voidColumn={voidColumn}
        voidRow={voidRow}
        emptySelectedArea={emptySelectedArea}
        emptySelectedAsAisle={emptySelectedAsAisle}
        emptySeatAt={emptySeatAt}
        emptySeatAtAsAisle={emptySeatAtAsAisle}
        restoreColumn={restoreColumn}
        naming={naming}
        copyZone={copyZone}
        pasteZone={pasteZone}
        duplicateZone={duplicateZone}
      />

      <EditorDialog
        open={Boolean(editorDialog)}
        title={editorDialog?.title || 'Bilgi'}
        message={editorDialog?.message || ''}
        showCancel={editorDialog?.type === 'confirm'}
        danger={Boolean(editorDialog?.danger)}
        confirmLabel={
          editorDialog?.type === 'confirm' ? editorDialog?.confirmLabel || 'Evet' : 'Tamam'
        }
        cancelLabel="İptal"
        onConfirm={() => {
          const resolve = editorDialog?.resolve;
          setEditorDialog(null);
          resolve && resolve(true);
        }}
        onCancel={() => {
          const resolve = editorDialog?.resolve;
          setEditorDialog(null);
          resolve && resolve(false);
        }}
      />

      <RenameSeatsModal
        open={Boolean(renameModal)}
        seatCount={renameModal?.keys?.length || 1}
        suggestedLabel={renameModal?.suggestedLabel || ''}
        onClose={() => setRenameModal(null)}
        onApply={applyRenameSeats}
      />
    </div>
  );
}
